
import { useState, useRef, useCallback, useEffect } from 'react';
import { Crop, PixelCrop } from 'react-image-crop';

interface UseCropperLogicProps {
  imageFile: File | null;
  zoom: number;
}

export const useCropperLogic = ({ imageFile, zoom }: UseCropperLogicProps) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imageSrc, setImageSrc] = useState<string>('');
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  const calculateSquareCrop = useCallback((image: HTMLImageElement): Crop => {
    const { width, height } = image;
    
    const squareSize = Math.min(width, height);
    const widthPercent = (squareSize / width) * 100;
    const heightPercent = (squareSize / height) * 100;
    const xPercent = (100 - widthPercent) / 2;
    const yPercent = (100 - heightPercent) / 2;
    
    return {
      unit: '%' as const,
      width: widthPercent,
      height: heightPercent,
      x: xPercent,
      y: yPercent,
    };
  }, []);

  const onImageLoad = useCallback((img: HTMLImageElement) => {
    imgRef.current = img;
    const squareCrop = calculateSquareCrop(img);
    setCrop(squareCrop);
  }, [calculateSquareCrop]);

  const getCroppedImg = useCallback(async (): Promise<Blob | null> => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const outputSize = 512;
    canvas.width = outputSize;
    canvas.height = outputSize;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, outputSize, outputSize);

    const zoomFactor = zoom;
    
    ctx.drawImage(
      image,
      (completedCrop.x * scaleX) / zoomFactor,
      (completedCrop.y * scaleY) / zoomFactor,
      (completedCrop.width * scaleX) / zoomFactor,
      (completedCrop.height * scaleY) / zoomFactor,
      0,
      0,
      outputSize,
      outputSize
    );

    return new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        }
      }, 'image/jpeg', 0.9);
    });
  }, [completedCrop, zoom]);

  const handleReset = useCallback(() => {
    if (imgRef.current) {
      const squareCrop = calculateSquareCrop(imgRef.current);
      setCrop(squareCrop);
    }
  }, [calculateSquareCrop]);

  return {
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    imageSrc,
    imgRef,
    canvasRef,
    onImageLoad,
    getCroppedImg,
    handleReset
  };
};
